import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormFeedback,
  Container,
  Button,
} from "reactstrap";
import { useFormik, FormikProvider } from "formik";
import { buildSchema } from "./schema";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import {
  getPettyWork,
  createPettyWork,
  updatePettyWork,
} from "features/PettyWorks/services/PettyWorkService";
import type {
  PettyWork,
  PettyWorkBoqLinePayload,
  PettyWorkItem,
} from "features/PettyWorks/types/petty-work.types";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getClientOptions } from "features/Clients/services/ClientService";
import {
  getPriorityOptions,
  getSourceChannelOptions,
} from "features/Meta/services/MetaService";

import { getDocumentTitle } from "utils/helpers";
import BoqItemsFieldArray from "components/common/BoqItemsFieldArray";
import {
  PettyWorkStatus,
  pettyWorkStatusMap,
} from "features/PettyWorks/constants/petty-work.constants";
import { pettyWorkLineQuantity } from "features/PettyWorks/utils/petty-work.utils";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import AppFlatpickr from "components/common/AppFlatpickr";
import moment from "moment";
import StatusBadge from "components/common/StatusBadge";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

function formRowToApiLine(row: {
  productId: string | number | "";
  description?: string;
  unit?: string;
  rate?: number | string | "";
  quantity: number | string;
  type: string;
}): PettyWorkBoqLinePayload {
  const qty = Number(row.quantity);
  const type = row.type as PettyWorkBoqLinePayload["type"];
  const pid =
    row.productId !== "" && row.productId != null ? Number(row.productId) : NaN;
  const hasProduct = Number.isFinite(pid) && pid > 0;

  if (hasProduct) {
    const line: PettyWorkBoqLinePayload = {
      productId: pid,
      quantity: qty,
      type,
    };
    const unit = String(row.unit ?? "").trim();
    const rate =
      row.rate !== "" && row.rate != null ? Number(row.rate) : undefined;
    if (unit) line.unit = unit;
    if (rate != null && Number.isFinite(rate)) line.rate = rate;
    const desc = String(row.description ?? "").trim();
    if (desc) line.description = desc;
    return line;
  }

  return {
    quantity: qty,
    type,
    description: String(row.description ?? "").trim(),
    unit: String(row.unit ?? "").trim(),
    rate: Number(row.rate),
  };
}

const PettyWorkForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { can } = useAccess();
  const canAddClient = can(Permissions.CLIENT.CREATE);
  const [record, setRecord] = useState<PettyWork>();

  const breadcrumbTitle =
    isEdit && record?.code?.trim()
      ? `Edit · ${record.code.trim()}`
      : isEdit
        ? "Edit petty work"
        : "Create petty work";

  document.title = getDocumentTitle(breadcrumbTitle);

  const navigate = useNavigate();

  const { id } = useParams();

  const boqEditable = !isEdit || record?.status === PettyWorkStatus.NEW;

  const validationSchema = useMemo(() => buildSchema(), []);

  const emptyPettyBoqRow = () => ({
    type: QuotationItemType.MATERIAL,
    description: "",
    productId: "",
    lineId: undefined as number | undefined,
    quantity: 1,
    unit: "",
    rate: 0,
  });

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: record?.title ?? "",
      code: record?.code ?? "",
      sourceChannelId: record?.sourceChannelId ?? "",
      description: record?.description ?? "",
      priorityId: record?.priorityId ?? "",
      clientId: record?.clientId ?? "",
      plannedStartDate: record?.plannedStartDate ?? "",
      plannedEndDate: record?.plannedEndDate ?? "",
      items: record?.items?.length
        ? record.items.map((item: PettyWorkItem) => ({
            type:
              (item.type as QuotationItemType) ?? QuotationItemType.MATERIAL,
            description: item.description ?? "",
            productId: item.productId ?? "",
            lineId:
              item.id != null &&
              Number.isFinite(Number(item.id)) &&
              Number(item.id) > 0
                ? Number(item.id)
                : undefined,
            quantity: pettyWorkLineQuantity(item),
            unit: item.unit ?? "",
            rate:
              item.rate != null ? item.rate : (item.product?.standardRate ?? 0),
          }))
        : [emptyPettyBoqRow()],
      agreedAmount: record?.agreedAmount ?? "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const codeValue = values.code?.trim() || undefined;

      const header = {
        title: values.title,
        ...(codeValue !== undefined ? { code: codeValue } : {}),
        sourceChannelId:
          values.sourceChannelId !== "" && values.sourceChannelId != null
            ? Number(values.sourceChannelId)
            : null,
        description: values.description?.trim()
          ? values.description.trim()
          : null,
        priorityId: Number(values.priorityId),
        clientId: Number(values.clientId),
        plannedStartDate: values.plannedStartDate?.trim() || null,
        plannedEndDate: values.plannedEndDate?.trim() || null,
        agreedAmount:
          values.agreedAmount === "" || values.agreedAmount == null
            ? null
            : Number(values.agreedAmount),
      };

      const itemsPayload = values.items.map((row: any) =>
        formRowToApiLine(row),
      );

      let response;
      try {
        if (isEdit) {
          const payload =
            record?.status === PettyWorkStatus.NEW
              ? { ...header, items: itemsPayload }
              : header;
          response = await updatePettyWork(Number(id), payload);
        } else {
          response = await createPettyWork({
            ...header,
            items: itemsPayload,
          });
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        } else {
          toast.error(response.message);
        }
      } catch (err) {
        console.log(err);
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? (err as Error).message,
        );
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    const fetchRecord = async () => {
      try {
        const response = await getPettyWork(Number(id));
        const { success, message, data } = response;
        if (success) {
          setRecord(data);
        }
        console.log(message);
      } catch (err) {
        console.warn(err);
      }
    };

    void fetchRecord();
  }, [id, isEdit]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={breadcrumbTitle}
            pageTitle="Petty works"
            items={[{ label: "Petty works", path: "/petty-works" }]}
          />
          <FormikProvider value={validation}>
            <Form
              className="needs-validation"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Row className="justify-content-center">
                <Col>
                  <Card className="shadow-none border">
                    <CardHeader className="border-bottom bg-transparent pb-3">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h5 className="card-title mb-0">
                          {isEdit ? "Edit petty work" : "Create petty work"}
                        </h5>
                        {record ? (
                          <StatusBadge value={record.status} map={pettyWorkStatusMap} />
                        ) : null}
                      </div>
                      <p className="text-muted mb-0 small">
                        {isEdit
                          ? record?.code?.trim()
                            ? `Update the title, schedule, priority, channel, and description for ${record.code.trim()}.`
                            : "Update the title, schedule, priority, source channel, and description."
                          : "Give this petty work a clear title, optional planned time, urgency, how the client found you, and what work is needed."}
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Details
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={4}>
                          <Label htmlFor="code" className="form-label">
                            Petty work code{" "}
                            <span className="text-muted fw-normal">
                              (optional)
                            </span>
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Auto-generated if blank"
                            id="code"
                            name="code"
                            readOnly={isEdit && !boqEditable}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.code || ""}
                            invalid={
                              !!(
                                validation.touched.code &&
                                validation.errors.code
                              )
                            }
                          />
                          {isEdit && !boqEditable ? (
                            <small className="text-muted">
                              Code can only be set while the petty work is in
                              New status.
                            </small>
                          ) : null}
                          {validation.touched.code &&
                          validation.errors.code ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.code}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={12}>
                          <Label htmlFor="title" className="form-label">
                            Title
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter title"
                            id="title"
                            name="title"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.title || ""}
                            invalid={
                              validation.touched.title &&
                              validation.errors.title
                                ? true
                                : false
                            }
                          />
                          {validation.touched.title &&
                          validation.errors.title ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.title}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6}>
                          <Label htmlFor="ForminputState" className="form-label">
                            Priority
                          </Label>
                          <AsyncPaginatedSelect
                            fetchOptions={getPriorityOptions}
                            placeholder="Search Priority..."
                            value={validation.values.priorityId}
                            onChange={(val: any) => {
                              validation.setFieldValue(
                                "priorityId",
                                val?.value,
                              );
                            }}
                            onBlur={() =>
                              validation.setFieldTouched("priorityId", true)
                            }
                            isInvalid={
                              !!validation.errors.priorityId &&
                              (validation.touched.priorityId ||
                                validation.submitCount > 0)
                            }
                          />
                          {validation.touched.priorityId &&
                          validation.errors.priorityId ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.priorityId}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Source channel</Label>
                          <AsyncPaginatedSelect
                            fetchOptions={getSourceChannelOptions}
                            placeholder="Search channel..."
                            defaultOption={
                              record?.sourceChannel
                                ? {
                                    value: record.sourceChannel.id,
                                    label: record.sourceChannel.name,
                                    code: record.sourceChannel.code,
                                  }
                                : undefined
                            }
                            value={validation.values.sourceChannelId}
                            onChange={(val: any) => {
                              validation.setFieldValue(
                                "sourceChannelId",
                                val?.value ?? "",
                              );
                            }}
                            onBlur={() =>
                              validation.setFieldTouched(
                                "sourceChannelId",
                                true,
                              )
                            }
                            isInvalid={
                              !!validation.errors.sourceChannelId &&
                              (validation.touched.sourceChannelId ||
                                validation.submitCount > 0)
                            }
                          />
                          {validation.touched.sourceChannelId &&
                          validation.errors.sourceChannelId ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.sourceChannelId}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={12}>
                          <Label htmlFor="discription" className="form-label">
                            Description
                          </Label>
                          <Input
                            type="textarea"
                            className="form-control "
                            id="description"
                            rows="3"
                            name="description"
                            placeholder="Enter description (optional)"
                            value={validation.values.description || ""}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.description &&
                              validation.errors.description
                                ? true
                                : false
                            }
                          />
                          {validation.touched.description &&
                          validation.errors.description ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.description}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Client
                      </div>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Label className="form-label">
                            Client{" "}
                            <span className="text-muted fw-normal">
                              — the account this petty work will be billed to
                            </span>
                          </Label>
                          <AsyncPaginatedSelect
                            fetchOptions={getClientOptions}
                            placeholder="Search clients…"
                            defaultOption={
                              record?.client && {
                                value: record.client.id,
                                label:
                                  record.client.name?.trim() ||
                                  record.client.company?.trim() ||
                                  `Client #${record.client.id}`,
                              }
                            }
                            value={validation.values.clientId}
                            onChange={(val: any) =>
                              validation.setFieldValue(
                                "clientId",
                                val?.value ?? "",
                              )
                            }
                            onBlur={() =>
                              validation.setFieldTouched("clientId", true)
                            }
                            isInvalid={
                              !!validation.errors.clientId &&
                              (validation.touched.clientId ||
                                validation.submitCount > 0)
                            }
                          />
                          {validation.touched.clientId &&
                          validation.errors.clientId ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(validation.errors.clientId)}
                            </FormFeedback>
                          ) : null}
                          {canAddClient ? (
                            <Link
                              to="/clients/create"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-primary text-decoration-none small fw-medium text-nowrap d-inline-block mt-1"
                            >
                              <i className="ri-add-line align-middle" /> Add new
                              client
                            </Link>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Schedule
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label htmlFor="plannedStartDate" className="form-label">
                            Planned start date
                          </Label>
                          <AppFlatpickr
                            name="plannedStartDate"
                            className={`form-control${validation.touched.plannedStartDate && validation.errors.plannedStartDate ? " is-invalid" : ""}`}
                            placeholder="Select start date"
                            options={{ dateFormat: "d M, Y" }}
                            value={
                              validation.values.plannedStartDate
                                ? new Date(validation.values.plannedStartDate)
                                : ""
                            }
                            onChange={(date: Date[]) =>
                              validation.setFieldValue(
                                "plannedStartDate",
                                date[0]
                                  ? moment(date[0]).format("YYYY-MM-DD")
                                  : "",
                              )
                            }
                          />
                          {validation.touched.plannedStartDate &&
                          validation.errors.plannedStartDate ? (
                            <FormFeedback>
                              {String(validation.errors.plannedStartDate)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6}>
                          <Label htmlFor="plannedEndDate" className="form-label">
                            Planned end date
                          </Label>
                          <AppFlatpickr
                            name="plannedEndDate"
                            className={`form-control${validation.touched.plannedEndDate && validation.errors.plannedEndDate ? " is-invalid" : ""}`}
                            placeholder="Select end date"
                            options={{ dateFormat: "d M, Y" }}
                            value={
                              validation.values.plannedEndDate
                                ? new Date(validation.values.plannedEndDate)
                                : ""
                            }
                            onChange={(date: Date[]) => {
                              void validation.setFieldValue(
                                "plannedEndDate",
                                date[0]
                                  ? moment(date[0]).format("YYYY-MM-DD")
                                  : "",
                              );
                              void validation.setFieldTouched(
                                "plannedEndDate",
                                true,
                              );
                            }}
                          />
                          {validation.touched.plannedEndDate &&
                          validation.errors.plannedEndDate ? (
                            <FormFeedback>
                              {String(validation.errors.plannedEndDate)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Financials
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label
                            className="form-label"
                            for="petty-work-agreed-amount"
                          >
                            Agreed amount
                          </Label>
                          <div className="input-group">
                            <span className="input-group-text text-muted">
                              <i className="ri-shake-hands-line" />
                            </span>
                            <Input
                              id="petty-work-agreed-amount"
                              type="number"
                              min={0}
                              step="0.01"
                              name="agreedAmount"
                              placeholder="0.00"
                              value={validation.values.agreedAmount ?? ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !!(
                                  validation.touched.agreedAmount &&
                                  validation.errors.agreedAmount
                                )
                              }
                            />
                            {validation.touched.agreedAmount &&
                            validation.errors.agreedAmount ? (
                              <FormFeedback>
                                {validation.errors.agreedAmount}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <small className="text-muted">
                            Independent of line items — set the job's value
                            directly.
                          </small>
                          <small className="text-muted d-block mt-1">
                            Reducing this value below the total of any active
                            client advances already collected against this
                            petty work will be blocked.
                          </small>
                          {isEdit && record ? (
                            <LinkedClientAdvancesTable
                              className="mt-3"
                              sourceType={ClientAdvanceSourceType.PETTY_WORK}
                              sourceId={record.id}
                            />
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                        Line items (BOQ)
                      </div>
                      <p className="text-muted small mb-3">
                        {boqEditable
                          ? "Same layout as quotations: type, description, unit, quantity, rate, and line amount."
                          : "Line items can only be changed while this petty work is in New status."}
                      </p>
                      <div className="border rounded p-3 bg-light bg-opacity-25">
                        <BoqItemsFieldArray
                          validation={validation}
                          readOnly={!boqEditable}
                          addLineLabel="Add line item"
                          getDefaultRow={emptyPettyBoqRow}
                          embedded
                        />
                      </div>
                    </CardBody>
                    <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                      <BackButton />
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={validation.isSubmitting}
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            {isEdit ? "Saving…" : "Creating…"}
                          </>
                        ) : isEdit ? (
                          "Save changes"
                        ) : (
                          "Create petty work"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Form>
          </FormikProvider>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PettyWorkForm;
